import { Injectable } from '@angular/core'
import { DeviceDataCollectorService } from './device-data-collector.service'
import { CollectedData, ResolvedCollectedData } from './contracts'
import { filter, finalize, first, map, of, ReplaySubject, switchMap, take, tap } from 'rxjs'
import { StorageService } from '@sun-finance/utils'
import { AuthService } from '@features/auth/auth.service'
import { DecisionService } from '@features/risks-integrations/decision.service'

const PENDING_DATA_STORAGE_KEY = 'risks_integrations_pending_device_data'

@Injectable({
    providedIn: 'root',
})
export class RisksIntegrationsService {
    private deviceData$ = new ReplaySubject<CollectedData>(1)
    private submissionInProgress = false
    private started = false

    constructor(
        private deviceDataCollector: DeviceDataCollectorService,
        private decision: DecisionService,
        private storage: StorageService,
        private auth: AuthService,
    ) {
    }

    startCollectingDeviceData() {
        if (this.started) return

        this.started = true

        this.storage.clear(PENDING_DATA_STORAGE_KEY)
        this.deviceDataCollector
            .collectDeviceData()
            .subscribe(this.deviceData$)
    }

    submitCollectedDeviceData(): void {
        if (this.submissionInProgress || !this.started) return

        this.submissionInProgress = true
        this.started = false

        const pending = this.storage.get(PENDING_DATA_STORAGE_KEY)
        const deviceData$ = pending ? of(JSON.parse(pending) as ResolvedCollectedData) : this.deviceData$.pipe(
            switchMap(data => data.seonSession$),
            map(seonSession => ({ seonSession })),
            tap(data => {
                this.storage.set(PENDING_DATA_STORAGE_KEY, JSON.stringify(data))
            }),
        )

        deviceData$.pipe(
            switchMap(data => this.auth.authenticated$.pipe(
                filter(Boolean),
                first(),
                map(() => data)
            )),
            switchMap(data => this.decision.runDeviceDataWorkflow(data)),
            take(1),
            finalize(() => {
                this.submissionInProgress = false
            })
        ).subscribe(() => {
            this.storage.clear(PENDING_DATA_STORAGE_KEY)
        })
    }
}
